/**
 * Skeletons (Shared chrome board, turn 7b): where the layout of the result is
 * known before it arrives, a tint-neutral stand-in holds the space and the arc
 * is reserved for waits whose shape is unknown. Every piece is the neutral tint
 * on the current surface, so the same skeleton reads in both modes.
 */

function Bone({ className = "" }: { className?: string }) {
  return <div className={"bg-tint-neutral animate-pulse " + className} aria-hidden />;
}

/** A comment while the thread loads: avatar, name line, two text lines. */
export function CommentSkeleton({ count = 3 }: { count?: number }) {
  return (
    <div data-testid="comments-skeleton" className="flex flex-col gap-[17px]">
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="flex gap-2.5">
          <Bone className="w-8 h-8 rounded-pill flex-none" />
          <div className="flex-1 min-w-0 flex flex-col gap-1.5 pt-0.5">
            <Bone className="h-3 w-24 rounded-md" />
            <Bone className="h-3 w-full rounded-md" />
            <Bone className="h-3 w-3/5 rounded-md" />
          </div>
        </div>
      ))}
    </div>
  );
}

/** A band card while the member grid loads — the same cell the card fills. */
export function MemberCardSkeleton({ count = 6 }: { count?: number }) {
  return (
    <>
      {Array.from({ length: count }, (_, i) => (
        <div key={i} data-testid="member-skeleton" className="rounded-panel overflow-hidden bg-card shadow-card">
          <Bone className="aspect-square w-full" />
          <div className="p-2.5 flex flex-col gap-1.5">
            <Bone className="h-3.5 w-2/3 rounded-md" />
            <Bone className="h-3 w-1/2 rounded-md" />
          </div>
        </div>
      ))}
    </>
  );
}

/** A line of the chat-style member list, between the same hairlines. */
export function MemberRowSkeleton({ count = 6 }: { count?: number }) {
  return (
    <>
      {Array.from({ length: count }, (_, i) => (
        <div key={i} data-testid="member-row-skeleton" className="flex items-center gap-3 px-4 py-3">
          <Bone className="w-11 h-11 rounded-pill flex-none" />
          <div className="flex-1 min-w-0 flex flex-col gap-1.5">
            <Bone className="h-3.5 w-32 rounded-md" />
            <Bone className="h-3 w-20 rounded-md" />
          </div>
        </div>
      ))}
    </>
  );
}

/** A short run of message bubbles while older history is fetched. */
export function MessagesSkeleton() {
  return (
    <div data-testid="messages-skeleton" className="flex flex-col gap-2 py-2" aria-hidden>
      <div className="flex gap-2 items-end">
        <Bone className="w-7 h-7 rounded-pill flex-none" />
        <Bone className="h-9 w-48 max-w-[70%] rounded-[16px]" />
      </div>
      <div className="flex justify-end">
        <Bone className="h-9 w-36 max-w-[60%] rounded-[16px]" />
      </div>
      <div className="flex gap-2 items-end">
        <Bone className="w-7 h-7 rounded-pill flex-none" />
        <Bone className="h-9 w-56 max-w-[75%] rounded-[16px]" />
      </div>
    </div>
  );
}
